import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { trpc } from "@/lib/trpc";
import { Download, FileText, Calendar, TrendingUp } from "lucide-react";
import { useState } from "react";
import { useLocation } from "wouter";
import { toast } from "sonner";
import { format } from "date-fns";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Switch } from "@/components/ui/switch";
import { AlertCircle } from "lucide-react";

interface AllWellsReportDay {
  day: number;
  date: string;
  production: Record<number, number>;
  metadata: Record<number, { hasCorrections?: boolean; isLocked?: boolean }>;
  dailyTotalMG: number;
}

interface AllWellsReport {
  year: number;
  month: number;
  wells: Array<{
    id: number;
    wellName: string;
  }>;
  days: AllWellsReportDay[];
  wellTotalsMG: Record<number, number>;
  maxDay: {
    day: number;
    totalMG: number;
    totalGallons: number;
  } | null;
  monthlyTotalMG: number;
}

export default function AllWellsProduction() {
  const [, navigate] = useLocation();

  const [year, setYear] = useState(new Date().getFullYear().toString());
  const [month, setMonth] = useState((new Date().getMonth() + 1).toString());
  const [hideEmptyWells, setHideEmptyWells] = useState(false);

  const { data: report, refetch } = trpc.reports.monthlyAllWells.useQuery(
    {
      year: parseInt(year),
      month: parseInt(month),
    },
    {
      enabled: !!year && !!month,
    }
  );

  const handleGenerate = () => {
    refetch();
  };

  const handleExportCSV = () => {
    if (!report) {
      toast.error("No data to export");
      return;
    }

    const headers = ["Day", "Date", ...wellsToShow.map(w => w.wellName), "Daily Total (MG)"];
    const rows = report.days.map(day => [
      day.day,
      day.date,
      ...wellsToShow.map(well => {
        const value = (day.production[well.id] || 0).toFixed(3);
        const marker = day.metadata[well.id] ? '*' : '';
        return value + marker;
      }),
      day.dailyTotalMG.toFixed(3),
    ]);
    const csv = [headers, ...rows, ["", "* = Corrected/Locked value", "", ...Array(wellsToShow.length).fill("")]].map(row => row.join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `all-wells-production-${year}-${month.padStart(2, '0')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("CSV exported successfully");
  };

  if (!report) {
    return (
      <div className="min-h-screen bg-background">
        <header className="border-b bg-card sticky top-0 z-10">
          <div className="container py-4">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="sm" onClick={() => navigate("/")}>
                ← Back to Dashboard
              </Button>
              <h1 className="text-xl font-bold">Monthly All-Wells Production Report</h1>
            </div>
          </div>
        </header>
        <main className="container py-6 max-w-7xl">
          <Card>
            <CardContent className="py-12 text-center">
              <p className="text-muted-foreground">Loading report...</p>
            </CardContent>
          </Card>
        </main>
      </div>
    );
  }

  const monthName = format(new Date(report.year, report.month - 1, 1), "MMMM yyyy");

  const wellsToShow = hideEmptyWells 
    ? report.wells.filter(w => report.wellTotalsMG[w.id] > 0)
    : report.wells;

  const missingDaysCounts: Record<number, number> = {};
  for (const well of wellsToShow) {
    let count = 0;
    for (const day of report.days) {
      if (day.production[well.id] === 0) count++;
    }
    missingDaysCounts[well.id] = count;
  }

  return (
    <div className="min-h-screen bg-background print:bg-white">
      <style jsx global>{`
        @media print {
          .no-print { display: none !important; }
          table { font-size: 9pt; width: 100%; page-break-inside: avoid; }
          th, td { border: 1px solid #000; padding: 4px; }
          .print-title { font-size: 16pt; font-weight: bold; text-align: center; }
        }
      `}</style>
      <header className="border-b bg-card sticky top-0 z-10 print:no-print">
        <div className="container py-4">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={() => navigate("/")}>
              ← Back to Dashboard
            </Button>
            <h1 className="text-xl font-bold">Monthly All-Wells Production Report</h1>
          </div>
        </div>
      </header>

      <main className="container py-6 max-w-7xl">
        {/* Controls */}
        <Card className="mb-8 print:no-print">
          <CardHeader>
            <CardTitle>Select Month</CardTitle>
            <CardDescription>Generate matrix view of daily production across all wells (MG)</CardDescription>
          </CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label htmlFor="year">Year</Label>
              <Input
                id="year"
                type="number"
                value={year}
                onChange={(e) => setYear(e.target.value)}
                min="2020"
                max="2050"
              />
            </div>
            <div>
              <Label htmlFor="month">Month</Label>
              <Select value={month} onValueChange={setMonth}>
                <SelectTrigger id="month">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Array.from({ length: 12 }, (_, i) => (
                    <SelectItem key={i + 1} value={(i + 1).toString()}>
                      {format(new Date(2000, i), "MMMM")}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex flex-col gap-2">
              <div className="flex items-center space-x-2">
                <Switch id="hide-empty" checked={hideEmptyWells} onCheckedChange={setHideEmptyWells} />
                <Label htmlFor="hide-empty">Only show wells with data</Label>
              </div>
              <Button onClick={handleGenerate} className="w-full">
                <Calendar className="w-4 h-4 mr-2" />
                Generate Report
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8 print:no-print">
          <Card>
            <CardContent className="p-6">
              <div className="text-2xl font-bold text-primary">{report.monthlyTotalMG.toFixed(3)} MG</div>
              <p className="text-sm text-muted-foreground">Monthly Total</p>
            </CardContent>
          </Card>
          {report.maxDay && (
            <Card>
              <CardContent className="p-6">
                <div className="text-2xl font-bold text-primary">{report.maxDay.totalMG.toFixed(3)} MG</div>
                <p className="text-sm text-muted-foreground">Max Day ({report.maxDay.day})</p>
                <p className="text-xs text-muted-foreground">{report.maxDay.totalGallons.toLocaleString()} gallons</p>
              </CardContent>
            </Card>
          )}
          <Card>
            <CardContent className="p-6">
              <div className="text-2xl font-bold">{report.wells.length} Wells</div>
              <p className="text-sm text-muted-foreground">Active Wells Included</p>
            </CardContent>
          </Card>
        </div>

        {/* Export */}
        <div className="flex gap-2 mb-8 print:no-print">
          <Button variant="outline" onClick={handleExportCSV}>
            <Download className="w-4 h-4 mr-2" />
            Export CSV
          </Button>
          <Button onClick={() => window.print()} className="no-print">
            <FileText className="w-4 h-4 mr-2" />
            Print PDF
          </Button>
        </div>

        {/* Matrix Table */}
        <Card>
          <CardHeader className="print-title pb-4">
            <CardTitle className="text-center">Monthly All-Wells Production Report</CardTitle>
            <CardDescription className="text-center">{monthName}</CardDescription>
            <p className="text-center text-sm text-muted-foreground">All values in Million Gallons (MG)</p>
          </CardHeader>
          <CardContent className="overflow-x-auto">
            <TooltipProvider>
              <table className="w-full table-auto border-collapse">
                <thead>
                  <tr className="bg-muted/50">
                    <th className="border p-2 text-left font-semibold">Day</th>
                    {wellsToShow.map((well) => (
                      <th key={well.id} className="border p-2 text-center font-semibold min-w-[120px]">
                        {well.wellName}
                      </th>
                    ))}
                    <th className="border p-2 text-right font-semibold">Daily Total</th>
                  </tr>
                </thead>
                <tbody>
                  {report.days.map((day) => (
                    <tr key={day.date} className="hover:bg-muted/50">
                      <td className="border p-2 font-medium">{day.day}</td>
                      {wellsToShow.map((well) => {
                        const value = day.production[well.id] || 0;
                        const meta = day.metadata[well.id];
                        const hasFlag = meta && (meta.hasCorrections || meta.isLocked);
                        return (
                          <td key={well.id} className="border p-2 text-center text-sm relative" style={{ 
                            backgroundColor: value === 0 ? '#f8f9fa' : 'transparent' 
                          }}>
                            {value.toFixed(3)}
                            {hasFlag && (
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <AlertCircle className="h-3 w-3 text-orange-500 absolute right-1 top-1/2 -translate-y-1/2" />
                                </TooltipTrigger>
                                <TooltipContent side="right">
                                  {meta?.hasCorrections && <div>Corrected</div>}
                                  {meta?.isLocked && <div>Locked</div>}
                                </TooltipContent>
                              </Tooltip>
                            )}
                          </td>
                        );
                      })}
                      <td className="border p-2 text-right font-semibold">
                        {day.dailyTotalMG.toFixed(3)}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <td className="border p-2 font-medium">Missing Days</td>
                    {wellsToShow.map((well) => (
                      <td key={well.id} className="border p-2 text-center">
                        {missingDaysCounts[well.id]}
                      </td>
                    ))}
                    <td className="border p-2 text-right">—</td>
                  </tr>
                  <tr className="bg-muted/75 font-bold">
                    <td className="border p-2">Monthly Total</td>
                    {wellsToShow.map((well) => (
                      <td key={well.id} className="border p-2 text-center">
                        {report.wellTotalsMG[well.id]?.toFixed(3) || '0.000'}
                      </td>
                    ))}
                    <td className="border p-2 text-right">
                      {report.monthlyTotalMG.toFixed(3)}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </TooltipProvider>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
